import { Machine } from "../GameComponents/Machine";
import { ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbols } from "../GameComponents/Symbols";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Symbol } from "../GameComponents/Symbol";
import { ReelExtended } from "./ReelExtended";
import { PlayList } from "../Components/Sounds/PlayList";
import { SymbolExtended } from "./SymbolExtended";
import { Settings } from "./Settings";
import { Tweens } from "../Components/Effects/Tweens";
import { ReelConfigurationAttributes } from "../Components/Configuration";
import { AppContext } from "../../Common/AppContext";
import { ModuleManager } from "../Managers/ModuleManager";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    protected noTrimContainer: ContainerElement;

    protected _wasForced: boolean = false;

    protected _preRoll: number;
    protected _preRollSpeed: number;

    private _quickStopDelay: number = 120;

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    protected configurate(node: Element): void {
        super.configurate(node);

        this._preRoll = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL))
            : 180;
        this._preRollSpeed = node.hasAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED)
            ? parseInt(node.getAttribute(ReelConfigurationAttributes.PRE_ROLL_SPEED))
            : 10;

        if (node.hasAttribute(MachineExtendedAttributes.QUICK_STOP_DELAY)) {
            this._quickStopDelay = parseInt(node.getAttribute(MachineExtendedAttributes.QUICK_STOP_DELAY));
        } else {
            this._quickStopDelay = this._stopDelay;
        }
    }

    /**
     * Initialize reference correct symbols and create reels.
     * @method initialize
     * @param symbols Symbols
     * @param reels Array<Array<number>>
     * @return void
     */
    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        winAnimator: WinAnimator,
        teasing?: ContainerElement,
        noTrimContainer?: ContainerElement,
    ): void {
        super.initialize(symbols, reels, winAnimator, teasing);

        this.noTrimContainer = noTrimContainer;
    }

    /**
     * Start spinning reels.
     * @method spinStart
     * @param longRotation boolean
     * @return void
     */
    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
    ): void {
        if (DEBUG) {
            this.log("spinStart");
        }

        ReelExtended.Tease = false;

        if (this.maskSprite != null && this._reelsContainer != null) {
            this._reelsContainer.addChild(this.maskSprite);
            this._reelsContainer.mask = this.maskSprite;
        }

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._startSpinTime = Date.now();

        this._wasForced = false;

        this.visible = true;
        this._reelsContainer.visible = true;

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        if (this._reels != null) {
            for (let i: number = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);
                        this._reels[i].currentSymbols = [];
                        for (let j = 0; j < this._visibleSymbolCount; j++) {
                            this.spinNoTrimSymbol(currentSymbols[i][j], i);
                            this._reels[i].currentSymbols.push(currentSymbols[i][j]);
                            currentSymbols[i][j] = null;
                        }
                        this._reels[i].showSymbols(useTransform);
                        this._reels[i].spinStart();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            if (longRotation != null && longRotation) {
                this._stopTimer.start(this._minSpinRotationLong);
            } else {
                this._stopTimer.start(this._minSpinRotation);
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }

        PlayList.play(this._teasing != null ? "SND_BaseRotationStart" : "SND_FSRStart");
        PlayList.loop("SND_BaseRotation", 10, true);
    }

    /**
     * Stop reels rotation.
     * @method spinStop
     * @param event any - from timer
     * @param force boolean - default false used when spin_stop was dispatch
     * @param fromServer boolean - default false used when spin_stop was dispatch from server
     * @param legal
     * @return void
     */
    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            PlayList.stop("SND_BaseFSTeaser");

            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
            this._wasForced = true;
        }

        if (DEBUG) {
            this.log("spinStop " + this._wasForced);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (!(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }

            if (this._wasForced && this._teasing != null) {
                for (let key in this._teasing.childs) {
                    if (this._teasing.childs[key] instanceof SwitchElement) {
                        this._teasing.childs[key].goToCase("EMPTY");
                    }
                }
            }

            this._canTease2 = this._wasForced ? false : this.checkTeasing(2);
            this._canTease3 = this._wasForced ? false : this.checkTeasing(3);
            this._canTease4 = this._wasForced ? false : this.checkTeasing(4);

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            let stopDelay = this._stopDelay * i;
                            let useForceCustomDelay = false;

                            if (this._wasForced || (!this._canTease2 && !this._canTease3 && !this._canTease4)) {
                                this._reels[i].spinStop(
                                    this._wasForced,
                                    this._wasForced ? this._quickStopDelay * i : stopDelay,
                                    this._wasForced ? true : useForceCustomDelay,
                                );
                            } else {
                                if (this._canTease2 && this._canTease3 && this._canTease4) {
                                    if (i === 0 || i === 1) {
                                        this._reels[i].spinStop(
                                            this._wasForced,
                                            this._wasForced ? this._quickStopDelay * i : stopDelay,
                                            this._wasForced ? true : useForceCustomDelay,
                                        );
                                    }
                                } else if (this._canTease3 && this._canTease4) {
                                    if (i === 0 || i === 1 || i === 2) {
                                        this._reels[i].spinStop(
                                            this._wasForced,
                                            this._wasForced ? this._quickStopDelay * i : stopDelay,
                                            this._wasForced ? true : useForceCustomDelay,
                                        );
                                    }
                                } else if (this._canTease4) {
                                    if (i === 0 || i === 1 || i === 2 || i === 3) {
                                        this._reels[i].spinStop(
                                            this._wasForced,
                                            this._wasForced ? this._quickStopDelay * i : stopDelay,
                                            this._wasForced ? true : useForceCustomDelay,
                                        );
                                    }
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    public onReelStopped(reelIndex: number): void {
        super.onReelStopped(reelIndex);

        if (this._stoppedSymbolsArray[reelIndex].symbols.indexOf(Settings.SymbolWild) !== -1) {
            PlayList.play("SND_WildHit" + reelIndex);
        }

        if (this._stoppedReelIndexes === this._reelCount) {
            this.visible = false;
        }
    }

    protected spinNoTrimSymbol(symbol: Symbol, reelIndex: number): void {
        if (this.noTrimContainer) {
            if (symbol != null && Settings.SymbolsNoTrim.indexOf(symbol.id + 1) != -1) {
                const symbolClone = symbol.clone();
                symbolClone.position.x = symbol.position.x;
                symbolClone.position.y = symbol.position.y;
                this.noTrimContainer.addChild(symbolClone);

                symbolClone.goToCase(Symbol.CaseRotation);

                Tweens.positionTween(symbolClone, {
                    tweenDelay: this._startDelay * reelIndex,
                    tweenX: [symbolClone.x, symbolClone.x, symbolClone.x],
                    tweenY: [symbolClone.y, symbolClone.y - this._preRoll, symbolClone.y],
                    tweenDuration: this._preRollSpeed * 2,
                    callbackFunction: (): void => {
                        this.noTrimContainer.removeChild(symbolClone);
                    },
                });
            }
        }
    }

    protected tease(reelIndex: number): void {}
}

export const MachineExtendedAttributes = {
    QUICK_STOP_DELAY: "QuickStopDelay",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/